import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MotionImage, MotionLink } from '../ui/MotionPrimitives';

import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isPropertiesPage = location.pathname === '/properties';

  const desktopNavLinkBase = !isScrolled && isPropertiesPage
    ? 'text-black transition-colors hover:text-orange-400'
    : 'text-white transition-colors hover:text-orange-300';

  const mobileNavLinkBase = 'block w-full text-left text-white transition-colors hover:text-orange-300';

  return (
    <header className="fixed top-0 left-0 z-20 w-full px-4 pt-3 text-white md:px-8 md:pt-4">
      <div
        className={`flex w-full items-center justify-between rounded-full px-4 py-3 md:px-6 md:py-4 transition-colors duration-300 ${
          isScrolled ? 'bg-black/90 backdrop-blur-sm' : 'bg-transparent'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center mr-[2px]">
          <MotionImage
            src="/logo/Hogar%20Dorado%20House%20Logo%201.svg"
            alt="Hogar Dorado House logo"
            className="h-[72px] w-auto"
          />
        </div>

        {/* Desktop nav */}
        <nav className="ml-auto hidden md:block">
          <ul className="flex items-center gap-[3rem] text-[15px] font-medium">
            <li>
              <MotionLink
                to="/"
                className={`${desktopNavLinkBase} ${
                  location.pathname === '/' ? 'relative after:absolute after:left-0 after:top-full after:mt-1 after:block after:h-[2px] after:w-full after:bg-orange-400' : ''
                }`}
              >
                Home
              </MotionLink>
            </li>
            <li>
              <MotionLink
                to="/about"
                className={`${desktopNavLinkBase} ${
                  location.pathname === '/about' ? 'relative after:absolute after:left-0 after:top-full after:mt-1 after:block after:h-[2px] after:w-full after:bg-orange-400' : ''
                }`}
              >
                About Us
              </MotionLink>
            </li>
            <li>
              <MotionLink
                to="/services"
                className={`${desktopNavLinkBase} ${
                  location.pathname === '/services' ? 'relative after:absolute after:left-0 after:top-full after:mt-1 after:block after:h-[2px] after:w-full after:bg-orange-400' : ''
                }`}
              >
                Services
              </MotionLink>
            </li>
            <li>
              <MotionLink
                to="/properties"
                className={`${desktopNavLinkBase} ${
                  location.pathname === '/properties' ? 'relative after:absolute after:left-0 after:top-full after:mt-1 after:block after:h-[2px] after:w-full after:bg-orange-400' : ''
                }`}
              >
                Properties
              </MotionLink>
            </li>
            <li>
              <MotionLink
                to="/contact"
                className="rounded-full border border-[#D88546] bg-[#D88546] px-5 py-1.5 text-sm font-semibold text-black shadow-sm transition-colors hover:bg-[#e59252] hover:border-[#e59252]"
              >
                Get Intouch
              </MotionLink>
            </li>
          </ul>
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="ml-auto inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white md:hidden"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle navigation menu"
        >
          <span className="sr-only">Toggle navigation menu</span>
          {isOpen ? (
            <CloseOutlinedIcon className="h-6 w-6" />
          ) : (
            <MenuOutlinedIcon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`${isOpen ? 'block' : 'hidden'} mt-2 rounded-2xl bg-black/95 px-4 pt-3 pb-6 shadow-lg md:hidden`}
      >
        <ul className="space-y-3 text-base font-medium">
          <li>
            <MotionLink to="/" className={mobileNavLinkBase} onClick={() => setIsOpen(false)}>
              Home
            </MotionLink>
          </li>
          <li>
            <MotionLink to="/about" className={mobileNavLinkBase} onClick={() => setIsOpen(false)}>
              About Us
            </MotionLink>
          </li>
          <li>
            <MotionLink
              to="/services"
              className={mobileNavLinkBase}
              onClick={() => setIsOpen(false)}
            >
              Services
            </MotionLink>
          </li>
          <li>
            <MotionLink
              to="/properties"
              className={mobileNavLinkBase}
              onClick={() => setIsOpen(false)}
            >
              Properties
            </MotionLink>
          </li>
          <li>
            <MotionLink
              to="/contact"
              className="mt-2 block w-full rounded-full border border-[#D88546] bg-[#D88546] px-6 py-2 text-center font-semibold text-black shadow-sm transition-colors hover:bg-[#e59252] hover:border-[#e59252]"
              onClick={() => setIsOpen(false)}
            >
              Get Intouch
            </MotionLink>
          </li>
        </ul>
      </div>
    </header>
  );
};

export default Header;